import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import resolveOrderRef from "@/lib/resolveOrderRef";
import { useAuth } from "@/lib/AuthContext";

/**
 * Resolver page for short / external deep-links.
 *
 * Accepts any of three identifier forms in `:ref` and redirects to the
 * canonical `/orders/{uuid}` URL:
 *   • Local UUID  (e.g. e88e3f67-3ea7-480c-be08-54f3384d7487)
 *   • Order number (e.g. ORD-00124, case-insensitive)
 *   • Base44 external_id (24-char Mongo ObjectId hex)
 *
 * If the order isn't in the local DB yet but the user is admin/manager,
 * we transparently trigger a Base44 sync and retry once — fixes the case
 * where an external ordering system links to a brand-new order before
 * the periodic sync has picked it up.
 */
export default function OrderShortLink() {
  const { ref } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | syncing | not-found | error
  const canSync = user?.role === "admin" || user?.role === "manager";

  useEffect(() => {
    let cancelled = false;
    let syncIndicatorTimer = null;
    async function run() {
      // If the resolver runs longer than 400ms (i.e. the list-scan didn't
      // find it locally and we're about to sync), flip the UI to a
      // "Syncing from Base44…" state so the user understands the delay.
      syncIndicatorTimer = setTimeout(() => {
        if (!cancelled && canSync) setStatus("syncing");
      }, 400);
      try {
        const result = await resolveOrderRef(ref, { canSync });
        if (cancelled) return;
        if (result?.order?.id) {
          if (result.synced) toast.success("Found via Base44 sync");
          nav(`/orders/${result.order.id}`, { replace: true });
          return;
        }
        setStatus("not-found");
      } catch (err) {
        console.warn("OrderShortLink: resolve failed", err);
        if (!cancelled) setStatus("error");
      } finally {
        if (syncIndicatorTimer) clearTimeout(syncIndicatorTimer);
      }
    }
    run();
    return () => {
      cancelled = true;
      if (syncIndicatorTimer) clearTimeout(syncIndicatorTimer);
    };
  }, [ref, nav, canSync]);

  if (status === "loading") {
    return (
      <div className="text-zinc-500" data-testid="short-link-loading">
        Looking up order <span className="font-mono font-semibold">{ref}</span>…
      </div>
    );
  }

  if (status === "syncing") {
    return (
      <div className="text-zinc-500" data-testid="short-link-syncing">
        Syncing from Base44 to find <span className="font-mono font-semibold">{ref}</span>…
        <div className="text-xs text-zinc-400 mt-1">This can take a few seconds.</div>
      </div>
    );
  }

  if (status === "not-found") {
    return (
      <div className="space-y-3" data-testid="short-link-not-found">
        <h1 className="font-heading font-black text-2xl">Order not found</h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          No order matches <span className="font-mono font-semibold">{ref}</span>{canSync ? " — even after re-syncing from Base44" : ""}.
          {!canSync && " It may not be synced from Base44 yet — ask an admin to run a sync, or check your current Live / Test view mode."}
        </p>
        <Link to="/orders" className="text-[#C8102E] font-semibold hover:underline">
          Go to orders →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="short-link-error">
      <h1 className="font-heading font-black text-2xl">Couldn&apos;t open that order</h1>
      <p className="text-sm text-zinc-600 dark:text-zinc-300">
        Something went wrong while looking up <span className="font-mono font-semibold">{ref}</span>.
        Please try again, or open it from the Orders page.
      </p>
      <Link to="/orders" className="text-[#C8102E] font-semibold hover:underline">
        Go to orders →
      </Link>
    </div>
  );
}
